"use client";

import { IClosedInlineWidget } from "@/components/common/IClosedInlineWidget";
import {
  CollectionsRichText,
  type RichTextSegment,
} from "@/components/landing/CollectionsRichText";
import { CollectionsRotatingWord } from "@/components/landing/CollectionsRotatingWord";
import { WistiaVslEmbed } from "@/components/landing/WistiaVslEmbed";
import { Icon, ICON_TYPE } from "@/components/library/Icon";
import { useBlockAnchorId } from "@/puck/blockAnchors";
import { BookDemoButton } from "@/puck/BookDemoButton";
import {
  GPT_PAGE_MOCKUP_DEFAULT_PROPS,
  LpGptPageMockupBlock,
} from "@/puck/landing/gptPageMockupBlock";
import type {
  LpGptHeroBlockProps,
  LpHeroBlockProps,
} from "@/puck/types";
import { extractBlogMdxSource } from "@/utils/blogMdxSource";
import { buildIClosedSchedulerUrl } from "@/utils/iclosed";

export function LpHeroBlock({
  titleMain,
  titleConnector,
  titleSuffix,
  audiencePhrases,
  descLine1,
  descLine2,
  showVideo,
  videoMediaId,
  videoAutoplay,
  footnotes,
  isEditing,
}: Readonly<LpHeroBlockProps & { isEditing?: boolean }>) {
  return (
    <section className="flex justify-center px-4 pt-16 pb-12 md:pt-20">
      <div className="w-full max-w-5xl text-center">
        <h1 className="font-monument text-5xl font-bold leading-[1.05] tracking-tight text-black md:text-7xl">
          {titleMain}
          <br />
          {titleConnector ? (
            <span className="text-black/80">{titleConnector} </span>
          ) : null}
          <CollectionsRotatingWord
            // Puck contentEditable fields are React nodes in the Studio canvas.
            phrases={audiencePhrases.map((phrase) =>
              extractBlogMdxSource(phrase.text),
            )}
            suffix={titleSuffix}
          />
        </h1>

        <div className="mx-auto mt-6 max-w-2xl font-monument text-base text-gail-gray md:text-lg">
          <p>
            <CollectionsRichText
              segments={descLine1 as RichTextSegment[]}
              boldClassName="font-medium text-black"
            />
          </p>
          <p>
            <CollectionsRichText
              segments={descLine2 as RichTextSegment[]}
              boldClassName="font-medium text-black"
            />
          </p>
        </div>

        {showVideo ? (
          <div className="mx-auto mt-10 w-full max-w-4xl">
            <WistiaVslEmbed
              mediaId={videoMediaId}
              autoplay={isEditing ? false : videoAutoplay}
            />
          </div>
        ) : null}

        <p className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 font-monument text-sm text-gail-gray">
          {footnotes.map((note, index) => (
            <span
              key={`footnote-${index}`}
              className="inline-flex items-center gap-2"
            >
              {index > 0 ? (
                <span aria-hidden="true" className="text-gail-gray">
                  ·
                </span>
              ) : null}
              {note.text}
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}

export function LpGptHeroBlock({
  offerBadge,
  title,
  titleAccent,
  titleLine2 = "",
  subtitle,
  subtitleSegments = [],
  ctaLabel = "",
  pillsEyebrow = "",
  audiencePills = [],
  showMockup = false,
  showVideo,
  videoMediaId,
  videoAutoplay,
  showOffer,
  offerTitle,
  offerSubtitle,
  iclosedEventUrl,
  iclosedEventTitle,
  isEditing,
}: Readonly<LpGptHeroBlockProps & { isEditing?: boolean }>) {
  const schedulerUrl = buildIClosedSchedulerUrl({}, iclosedEventUrl);
  const bookDemoId = useBlockAnchorId("book-demo");
  const schedulerId = useBlockAnchorId("book-demo-scheduler");
  // Stored pages predate the toggles; missing values keep the original layout.
  const videoVisible = showVideo ?? true;
  const offerVisible = showOffer ?? true;

  return (
    <section className="flex justify-center px-4 pt-10 pb-16 md:pt-14">
      <div className="w-full max-w-5xl">
        <div className="mx-auto max-w-3xl text-center">
          {offerBadge ? (
            <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-gail-primary-light px-3.5 py-1.5 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-gail-primary">
              <span
                aria-hidden="true"
                className="h-1.5 w-1.5 rounded-full bg-gail-primary"
              />
              {offerBadge}
            </span>
          ) : null}
          <h1 className="font-monument text-5xl font-bold leading-[1.04] tracking-tight text-black md:text-7xl">
            {title} <span className="text-gail-primary">{titleAccent}</span>
            {titleLine2 ? (
              <>
                <br />
                {titleLine2}
              </>
            ) : null}
          </h1>
          <p className="mx-auto mt-5 max-w-2xl font-monument text-base leading-relaxed text-gail-gray md:text-lg">
            {subtitleSegments.length > 0 ? (
              <CollectionsRichText
                segments={subtitleSegments as RichTextSegment[]}
                boldClassName="font-medium text-black"
              />
            ) : (
              subtitle
            )}
          </p>

          {ctaLabel ? (
            <div className="mt-7">
              <BookDemoButton
                href="#book-demo"
                className="rounded-full bg-gail-primary! px-7 py-3.5 text-base hover:bg-gail-primary/90! active:bg-gail-primary/90!"
              >
                {ctaLabel}
              </BookDemoButton>
            </div>
          ) : null}

          {pillsEyebrow ? (
            <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.22em] text-gail-gray">
              {pillsEyebrow}
            </p>
          ) : null}

          {audiencePills.length > 0 ? (
            <ul
              className={`${pillsEyebrow ? "mt-4" : "mt-6"} flex flex-wrap items-center justify-center gap-2`}
            >
              {audiencePills.map((pill, index) => (
                <li
                  key={`pill-${index}`}
                  className="flex items-center gap-2 rounded-full border border-black/10 bg-white px-3.5 py-1.5 font-monument text-sm text-black"
                >
                  <Icon
                    icon="check"
                    type={ICON_TYPE.SOLID}
                    className="h-3 w-3 shrink-0 text-gail-primary"
                  />
                  {pill.text}
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        {showMockup ? (
          <div className="mt-12 w-full">
            <LpGptPageMockupBlock {...GPT_PAGE_MOCKUP_DEFAULT_PROPS} embedded />
          </div>
        ) : null}

        {videoVisible ? (
          <div className="mx-auto mt-10 w-full max-w-4xl">
            <WistiaVslEmbed
              mediaId={videoMediaId}
              autoplay={isEditing ? false : videoAutoplay}
            />
          </div>
        ) : null}

        {offerVisible ? (
          <div className="mt-12 w-full">
            <div
              id={bookDemoId}
              className="mx-auto w-full max-w-5xl scroll-mt-24"
            >
              <div className="rounded-t-2xl bg-gail-primary px-6 py-5 text-center">
                <p className="font-monument text-lg font-bold leading-snug text-white md:text-xl">
                  {offerTitle}
                </p>
                <p className="mt-1.5 font-monument text-sm leading-relaxed text-white">
                  {offerSubtitle}
                </p>
              </div>
              <div
                id={schedulerId}
                className="scroll-mt-24 rounded-b-2xl border border-t-0 border-black/10 bg-white px-3 pb-5 pt-4 shadow-[0_30px_70px_-40px_rgba(13,78,176,0.5)] md:px-4"
              >
                <IClosedInlineWidget
                  key={schedulerUrl}
                  url={schedulerUrl}
                  title={iclosedEventTitle}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </section>
  );
}
